import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'
import invariant from 'tiny-invariant'
import { useGetAnalyses } from '../../utils/api-client/api-hooks'
import { ToolSchema, type Tool } from '../../utils/api-client/api-schemas'
import { getToolName } from '../../utils/repository-utils'
import { ToolIcon } from '../third-party-branding/tool-icon'
import { ToolLogo } from '../third-party-branding/tool-logo'
import { TooltipWithHover } from '../tooltip'
import { StyledExpander, StyledItemIndicator } from './styled-dropdown-menu'
import * as styles from './tool-selector.css'

type ValueChangeHandler = Exclude<RadixDropdownMenu.DropdownMenuRadioGroupProps['onValueChange'], undefined>

export type ToolSelectorProps = {
  repositoryId: string
  selectedTool: Tool
  setSelectedTool: (tool: Tool) => unknown
  tools: readonly Tool[]
  /**
   * For views that carry no tool dimension at all — the threat model and everything derived from it is
   * repository-wide. Shown, so the reader keeps their bearings on which tool they came from, but inert,
   * because changing it here would change nothing on screen.
   */
  isDisabled?: boolean
  /** Why it is inert, shown in a tooltip on hover and focus. Without it a dead control is just a broken one. */
  disabledReason?: string
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({ repositoryId, selectedTool, ...rest }) => {
  const { lastSynced, isLoading: isLastSyncedLoading } = useLastSynced({ repositoryId, tool: selectedTool })

  return (
    <ToolSelectorPure
      lastSynced={lastSynced}
      isLastSyncedLoading={isLastSyncedLoading}
      selectedTool={selectedTool}
      {...rest}
    />
  )
}

export type ToolSelectorPureProps = Omit<ToolSelectorProps, 'repositoryId'> & {
  lastSynced?: DateTime<true>
  isLastSyncedLoading: boolean
}

export const ToolSelectorPure: React.FC<ToolSelectorPureProps> = ({
  lastSynced,
  isLastSyncedLoading,
  selectedTool,
  setSelectedTool,
  tools: tools,
  isDisabled = false,
  disabledReason,
}) => {
  const lastSyncedLabel = useMemo(() => {
    if (isLastSyncedLoading) {
      return '-'
    }

    return lastSynced?.toLocaleString(DateTime.DATETIME_SHORT) ?? 'never'
  }, [isLastSyncedLoading, lastSynced])

  const handleValueChange = useCallback<ValueChangeHandler>(
    value => setSelectedTool(ToolSchema.parse(value)),
    [setSelectedTool]
  )

  if (isDisabled) {
    // `aria-disabled` rather than `disabled`: a disabled button leaves the tab order entirely, so a
    // keyboard user would never reach the explanation for why it is inert. This stays focusable and
    // hoverable, and simply is not a menu trigger.
    const trigger = (
      <button
        type="button"
        aria-disabled
        aria-label={selectedTool}
        className={classNames(styles.trigger, styles.triggerDisabled)}
      >
        <div style={{ height: '24px', display: 'flex', alignItems: 'center' }}>
          <ToolLogo tool={selectedTool} size="medium+" />
        </div>
        <StyledExpander />
      </button>
    )

    // `side="bottom"` because the selector sits in the tab strip with the page header directly above
    // it; a tooltip on top would cover the repository name it is explaining the scope of.
    return disabledReason ? (
      <TooltipWithHover trigger={trigger} side="bottom" size="small" maxWidth={260}>
        {disabledReason}
      </TooltipWithHover>
    ) : (
      trigger
    )
  }

  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger className={styles.trigger} aria-label={selectedTool}>
        <div style={{ height: '24px', display: 'flex', alignItems: 'center' }}>
          <ToolLogo tool={selectedTool} size="medium+" />
        </div>
        <StyledExpander />
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content onCloseAutoFocus={e => e.preventDefault()} className={styles.content}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: styles.CONTENT_ADJUSTED_GAP, width: '100%' }}>
            <RadixDropdownMenu.Label className={styles.lastSyncedLabel}>
              Last synced: {lastSyncedLabel}
            </RadixDropdownMenu.Label>
            <RadixDropdownMenu.Separator className={styles.separator} />
            <RadixDropdownMenu.RadioGroup
              value={selectedTool}
              onValueChange={handleValueChange}
              className={styles.radioGroup}
            >
              {tools.map(tool => (
                <ToolSelectorRadioItem key={tool} tool={tool} />
              ))}
            </RadixDropdownMenu.RadioGroup>
          </div>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}

const ToolSelectorRadioItem: React.FC<{ tool: Tool }> = ({ tool }) => {
  return (
    <RadixDropdownMenu.RadioItem className={styles.radioItem} value={tool} aria-label={tool}>
      <div style={{ height: '24px', display: 'flex', alignItems: 'center' }}>
        <ToolIcon tool={tool} size="medium+" />
      </div>
      <span className={styles.radioItemLabel}>{getToolName(tool)}</span>
      <StyledItemIndicator />
    </RadixDropdownMenu.RadioItem>
  )
}

const useLastSynced = ({ repositoryId, tool }: { repositoryId: string; tool: Tool }) => {
  const { data: latestAnalysis, ...rest } = useGetAnalyses({
    page: {
      number: 0,
      size: 1,
    },
    filters: {
      repositoryIds: [repositoryId],
      tools: [tool],
    },
  })

  const lastSyncedTimestamp = latestAnalysis?._embedded.items[0]?.current_state.timestamp
  const lastSynced = lastSyncedTimestamp ? DateTime.fromISO(lastSyncedTimestamp) : undefined
  if (lastSynced) {
    invariant(lastSynced.isValid, 'lastSynced is invalid')
  }

  return {
    lastSynced,
    ...rest,
  }
}
